import { useCallback, useEffect, useMemo, useState } from 'react';
import { Dialog, Switch } from '@headlessui/react';
import { ArrowPathIcon, CheckCircleIcon, EyeIcon, KeyIcon, PencilIcon, PlusIcon, TrashIcon, XCircleIcon } from '@heroicons/react/24/outline';
import CopyButton from '../components/common/CopyButton';
import { useConfig } from '../providers/config-provider';
import { useApiClient } from '../lib/api';
import Pagination from '../components/common/Pagination';
import SortableTh from '../components/common/SortableTh';
import StatusPill from '../components/common/StatusPill';
import { useNotification } from '../providers/notification-provider';
import { useAuth } from '../providers/auth-provider';
import ContextHelpButton from '../components/help/ContextHelpButton';
import ContextHelpDrawer from '../components/help/ContextHelpDrawer';
import { INTEGRATIONS_HELP_SECTIONS } from '../components/help/waiterCallHelpContent';

const pageSize = 10;

const formatDateTime = (value) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return date.toLocaleString('pt-BR');
};

const scopesToString = (scopes) => {
  if (!Array.isArray(scopes) || scopes.length === 0) return 'calls:create';
  return scopes.join(', ');
};

const toDateTimeLocal = (value) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const local = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
  return local.toISOString().slice(0, 16);
};

const METHOD_OPTIONS = [
  { value: 'calls:create', label: 'Criar chamadas' },
  { value: 'calls:read', label: 'Visualizar chamadas (com status)' },
  { value: 'calls:claim', label: 'Assumir chamadas' },
  { value: 'integrations:companies:read', label: 'Listar empresas' },
  { value: 'integrations:restaurants:read', label: 'Listar restaurantes' },
  { value: 'integrations:tables:read', label: 'Listar mesas' },
  { value: 'integrations:tables:qr:read', label: 'Gerar QR PNG' }
];

const METHOD_LABEL_BY_SCOPE = METHOD_OPTIONS.reduce((acc, option) => {
  acc[option.value] = option.label;
  return acc;
}, {});

const getMethodMeta = (scope) => {
  const normalized = String(scope || '').trim().toLowerCase();
  const label = METHOD_LABEL_BY_SCOPE[normalized] || scope;
  const isWrite = /:(create|edit|update|delete|claim|resolve|revoke|rotate|activate)$/.test(normalized);
  return { label, isWrite };
};

const MethodPills = ({ scopes }) => {
  const normalizedScopes = Array.isArray(scopes) && scopes.length > 0 ? scopes : ['calls:create'];

  return (
    <div className="mt-1 flex flex-wrap gap-1.5">
      {normalizedScopes.map((scope) => {
        const meta = getMethodMeta(scope);
        return (
          <span
            key={scope}
            className={`inline-flex items-center gap-1 rounded-full px-2 py-1 text-[11px] font-semibold ${meta.isWrite ? 'bg-amber-100 text-amber-800' : 'bg-sky-100 text-sky-800'}`}
            title={String(scope)}
          >
            {meta.isWrite ? <PencilIcon className="h-3.5 w-3.5" /> : <EyeIcon className="h-3.5 w-3.5" />}
            <span className="whitespace-normal break-words">{meta.label}</span>
          </span>
        );
      })}
    </div>
  );
};

const IntegrationsPage = () => {
  const api = useApiClient();
  const { user } = useAuth();
  const { showToast, showConfirm } = useNotification();
  const { apiBase, companyId, restaurantId, setCompanyId, setRestaurantId } = useConfig();
  const isAdminMaster = user?.role === 'AdminMaster' || user?.isAdminMaster;

  const [credentials, setCredentials] = useState({ items: [], totalItems: 0, totalPages: 0 });
  const [companies, setCompanies] = useState([]);
  const [restaurants, setRestaurants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [page, setPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCompanyId, setSelectedCompanyId] = useState('');
  const [selectedRestaurantId, setSelectedRestaurantId] = useState('');
  const [includeInactive, setIncludeInactive] = useState(false);
  const [sortConfig, setSortConfig] = useState({ key: 'createdAt', direction: 'desc' });
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState('create');
  const [activeCredential, setActiveCredential] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    companyId: '',
    restaurantIds: [],
    expiresAt: '',
    scopes: ['calls:create'],
    isActive: true
  });
  const [formErrors, setFormErrors] = useState({
    companyId: '',
    name: '',
    scopes: ''
  });
  const [issuedSecret, setIssuedSecret] = useState(null);
  const [snackbar, setSnackbar] = useState(null);
  const [isHelpOpen, setIsHelpOpen] = useState(false);
  const [helpSectionKey, setHelpSectionKey] = useState(INTEGRATIONS_HELP_SECTIONS[0].key);

  const normalizedBase = useMemo(() => apiBase.replace(/\/+$/, ''), [apiBase]);

  const curlExample = useMemo(
    () => `# Exemplo GET: listar chamadas por status
METHOD="GET"
PATH_QUERY="/api/v1/integrations/calls?restaurantId=<restaurant-guid>&status=Claimed&page=1&pageSize=20"
TIMESTAMP="1714820400"
NONCE="req-001-abc123"
RAW_BODY=""

MESSAGE="$METHOD\n$PATH_QUERY\n$TIMESTAMP\n$RAW_BODY"
SIGNATURE="hex( HMACSHA256(API_SECRET, MESSAGE) )"

curl -X GET "${normalizedBase}$PATH_QUERY" \
  -H "X-Api-Key: <api-key>" \
  -H "X-Timestamp: $TIMESTAMP" \
  -H "X-Nonce: $NONCE" \
  -H "X-Signature: $SIGNATURE" \
  -H "Accept: application/json"`,
    [normalizedBase]
  );

  const messageTemplate = `message = METHOD + "\\n" + PATH_AND_QUERY + "\\n" + TIMESTAMP + "\\n" + RAW_BODY
signature = hex( HMACSHA256(secret, message) )

Headers obrigatórios:
- X-Api-Key
- X-Timestamp (ISO8601 UTC ou Unix epoch)
- X-Nonce (anti-replay)
- X-Signature

Observação: X-Nonce não entra na mensagem assinada.`;

  const loadCompanies = useCallback(async () => {
    try {
      const response = await api.get('/api/v1/integrations/credentials/companies');
      const companyItems = Array.isArray(response.data) ? response.data : [];
      setCompanies(companyItems);

      const preferredCompanyId = companyId || selectedCompanyId;
      const hasPreferred = preferredCompanyId && companyItems.some((item) => item.id === preferredCompanyId);
      if (hasPreferred) {
        setSelectedCompanyId(preferredCompanyId);
      } else if (companyItems.length > 0) {
        setSelectedCompanyId(companyItems[0].id);
      }
    } catch (error) {
      console.error(error);
      showToast('Não foi possível carregar empresas.', 'error');
    }
  }, [api, companyId, selectedCompanyId, showToast]);

  const loadRestaurants = useCallback(async (companyIdOverride) => {
    const targetCompanyId = companyIdOverride || selectedCompanyId;

    if (!targetCompanyId && isAdminMaster) {
      setRestaurants([]);
      return;
    }

    try {
      const response = await api.get('/api/v1/integrations/credentials/restaurants', {
        params: {
          companyId: targetCompanyId || undefined
        }
      });
      setRestaurants(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error(error);
      showToast('Não foi possível carregar restaurantes.', 'error');
    }
  }, [api, isAdminMaster, selectedCompanyId, showToast]);

  const loadCredentials = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get('/api/v1/integrations/credentials', {
        params: {
          companyId: isAdminMaster ? selectedCompanyId || undefined : undefined,
          page,
          pageSize,
          search: searchTerm || undefined,
          sortBy: sortConfig.key,
          sortDesc: sortConfig.direction === 'desc',
          includeInactive,
          restaurantId: selectedRestaurantId || undefined
        }
      });
      setCredentials(response.data || { items: [], totalItems: 0, totalPages: 0 });
    } catch (error) {
      console.error(error);
      showToast('Não foi possível carregar credenciais de integração.', 'error');
    } finally {
      setLoading(false);
    }
  }, [api, includeInactive, isAdminMaster, page, searchTerm, selectedCompanyId, selectedRestaurantId, showToast, sortConfig.direction, sortConfig.key]);

  useEffect(() => {
    document.title = 'Integrações | Orion WaiterCall';
    loadCompanies();
    loadRestaurants();
  }, [loadCompanies, loadRestaurants]);

  useEffect(() => {
    setSelectedCompanyId(companyId || '');
  }, [companyId]);

  useEffect(() => {
    setSelectedRestaurantId(restaurantId || '');
  }, [restaurantId]);

  useEffect(() => {
    loadRestaurants();
  }, [selectedCompanyId, loadRestaurants]);

  useEffect(() => {
    const timer = setTimeout(() => {
      loadCredentials();
    }, 250);
    return () => clearTimeout(timer);
  }, [loadCredentials]);

  const notify = (ok) => {
    setSnackbar(ok ? 'Conteúdo copiado.' : 'Não foi possível copiar.');
    setTimeout(() => setSnackbar(null), 2800);
  };

  const handleSort = (key) => {
    setPage(1);
    setSortConfig((current) => {
      if (current.key === key) {
        return { key, direction: current.direction === 'asc' ? 'desc' : 'asc' };
      }
      return { key, direction: 'asc' };
    });
  };

  const resetForm = (companyIdValue = selectedCompanyId) => {
    setFormData({
      name: '',
      companyId: companyIdValue || '',
      restaurantIds: [],
      expiresAt: '',
      scopes: ['calls:create'],
      isActive: true
    });
    setFormErrors({ companyId: '', name: '', scopes: '' });
  };

  const openCreate = () => {
    setModalMode('create');
    setActiveCredential(null);
    resetForm();
    setModalOpen(true);
  };

  const openEdit = (item) => {
    setModalMode('edit');
    setActiveCredential(item);
    setFormData({
      name: item.name || '',
      companyId: item.companyId || selectedCompanyId || '',
      restaurantIds: Array.isArray(item.restaurants) ? item.restaurants.map((restaurant) => restaurant.id) : [],
      expiresAt: toDateTimeLocal(item.expiresAt),
      scopes: Array.isArray(item.scopes) && item.scopes.length > 0 ? item.scopes : ['calls:create'],
      isActive: Boolean(item.isActive)
    });
    setFormErrors({ companyId: '', name: '', scopes: '' });
    setModalOpen(true);
  };

  const openView = (item) => {
    setModalMode('view');
    setActiveCredential(item);
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setActiveCredential(null);
    setSaving(false);
  };

  const toggleScope = (scopeValue) => {
    setFormData((current) => {
      const hasScope = current.scopes.includes(scopeValue);
      if (hasScope) {
        return {
          ...current,
          scopes: current.scopes.filter((scope) => scope !== scopeValue)
        };
      }
      return {
        ...current,
        scopes: [...current.scopes, scopeValue]
      };
    });
  };

  const toggleRestaurant = (restaurantId) => {
    setFormData((current) => {
      const hasRestaurant = current.restaurantIds.includes(restaurantId);
      if (hasRestaurant) {
        return {
          ...current,
          restaurantIds: current.restaurantIds.filter((id) => id !== restaurantId)
        };
      }
      return {
        ...current,
        restaurantIds: [...current.restaurantIds, restaurantId]
      };
    });
  };

  const submitCredential = async (event) => {
    event.preventDefault();

    const targetCompanyId = formData.companyId || selectedCompanyId;
    const nextErrors = {
      companyId: isAdminMaster && !targetCompanyId ? 'Selecione uma empresa.' : '',
      name: !formData.name.trim() ? 'Informe um nome para a credencial.' : '',
      scopes: formData.scopes.length === 0 ? 'Selecione ao menos um método permitido.' : ''
    };

    setFormErrors(nextErrors);

    if (nextErrors.companyId || nextErrors.name || nextErrors.scopes) {
      showToast('Preencha os campos obrigatórios destacados.', 'warning');
      return;
    }

    const willInactivate = modalMode === 'edit' && activeCredential?.isActive && formData.isActive === false;
    if (willInactivate) {
      const confirmed = await showConfirm({
        title: 'Inativar credencial',
        message: `Deseja inativar a credencial "${activeCredential.name}"?`,
        confirmText: 'Inativar',
        cancelText: 'Cancelar',
        type: 'warning'
      });

      if (!confirmed) {
        return;
      }
    }

    setSaving(true);
    try {
      const payload = {
        name: formData.name.trim(),
        companyId: targetCompanyId || null,
        restaurantIds: formData.restaurantIds,
        expiresAt: formData.expiresAt ? new Date(formData.expiresAt).toISOString() : null,
        scopes: formData.scopes,
        isActive: formData.isActive
      };

      if (modalMode === 'edit' && activeCredential) {
        await api.put(`/api/v1/integrations/credentials/${activeCredential.id}`, payload);
        closeModal();
        showToast('Credencial atualizada com sucesso.', 'success');
      } else {
        const response = await api.post('/api/v1/integrations/credentials', payload);
        setIssuedSecret(response.data);
        closeModal();
        showToast('Credencial gerada com sucesso.', 'success');
      }

      setPage(1);
      loadCredentials();
    } catch (error) {
      console.error(error);
      const message = error?.response?.data || 'Não foi possível salvar a credencial.';
      showToast(typeof message === 'string' ? message : 'Não foi possível salvar a credencial.', 'error');
      setSaving(false);
    }
  };

  const revokeCredential = async (item) => {
    const confirmed = await showConfirm({
      title: 'Revogar credencial',
      message: `Confirma revogar a credencial "${item.name}"?`,
      confirmText: 'Revogar',
      cancelText: 'Cancelar',
      type: 'warning'
    });

    if (!confirmed) return;

    try {
      await api.post(`/api/v1/integrations/credentials/${item.id}/revoke`);
      showToast('Credencial revogada com sucesso.', 'success');
      loadCredentials();
    } catch (error) {
      console.error(error);
      const message = error?.response?.data || 'Não foi possível revogar a credencial.';
      showToast(typeof message === 'string' ? message : 'Não foi possível revogar a credencial.', 'error');
    }
  };

  const activateCredential = async (item) => {
    try {
      await api.post(`/api/v1/integrations/credentials/${item.id}/activate`);
      showToast('Credencial reativada com sucesso.', 'success');
      loadCredentials();
    } catch (error) {
      console.error(error);
      const message = error?.response?.data || 'Não foi possível reativar a credencial.';
      showToast(typeof message === 'string' ? message : 'Não foi possível reativar a credencial.', 'error');
    }
  };

  const rotateCredential = async (item) => {
    const confirmed = await showConfirm({
      title: 'Rotacionar credencial',
      message: `Uma nova credencial será gerada e a credencial "${item.name}" será desativada. Deseja continuar?`,
      confirmText: 'Rotacionar',
      cancelText: 'Cancelar',
      type: 'warning'
    });

    if (!confirmed) return;

    try {
      const response = await api.post(`/api/v1/integrations/credentials/${item.id}/rotate`, {
        name: item.name,
        restaurantIds: Array.isArray(item.restaurants) ? item.restaurants.map((restaurant) => restaurant.id) : [],
        scopes: item.scopes,
        expiresAt: item.expiresAt
      });
      setIssuedSecret(response.data);
      showToast('Credencial rotacionada com sucesso.', 'success');
      setPage(1);
      loadCredentials();
    } catch (error) {
      console.error(error);
      const message = error?.response?.data || 'Não foi possível rotacionar a credencial.';
      showToast(typeof message === 'string' ? message : 'Não foi possível rotacionar a credencial.', 'error');
    }
  };

  const deleteCredential = async (item) => {
    const confirmed = await showConfirm({
      title: 'Excluir credencial',
      message: `A credencial "${item.name}" será removida permanentemente. Deseja continuar?`,
      confirmText: 'Excluir',
      cancelText: 'Cancelar',
      type: 'error'
    });

    if (!confirmed) return;

    try {
      await api.delete(`/api/v1/integrations/credentials/${item.id}`);
      showToast('Credencial excluída com sucesso.', 'success');
      setPage(1);
      loadCredentials();
    } catch (error) {
      console.error(error);
      const message = error?.response?.data || 'Não foi possível excluir a credencial.';
      showToast(typeof message === 'string' ? message : 'Não foi possível excluir a credencial.', 'error');
    }
  };

  const restaurantNameById = useMemo(() => {
    const lookup = new Map();
    restaurants.forEach((restaurant) => {
      lookup.set(restaurant.id, restaurant.name);
    });
    return lookup;
  }, [restaurants]);

  return (
    <section className="space-y-6">
      <div className="sm:flex sm:items-center">
        <div className="sm:flex-auto">
          <h1 className="text-xl font-semibold text-gray-900">Integrações</h1>
          <p className="mt-2 text-sm text-gray-700">Gerencie credenciais HMAC para integradores com auditoria e rotação segura.</p>
        </div>
        <div className="mt-4 flex items-center justify-end gap-2 sm:ml-16 sm:mt-0 sm:flex-none">
          <button
            type="button"
            onClick={openCreate}
            className="inline-flex flex-1 items-center justify-center rounded-md bg-indigo-600 px-3 py-2 text-center text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 sm:flex-none"
          >
            <PlusIcon className="mr-1 inline h-5 w-5" />
            Nova credencial
          </button>
          <ContextHelpButton
            label="Ajuda sobre integrações"
            onClick={() => {
              setHelpSectionKey('credentials');
              setIsHelpOpen(true);
            }}
          />
        </div>
      </div>

      <div className="grid gap-4 rounded-xl border border-slate-200 bg-white p-4 shadow-sm lg:items-end lg:grid-cols-[minmax(220px,1.4fr)_minmax(180px,1fr)_minmax(180px,1fr)_auto]">
        <div className="space-y-1 lg:min-w-0">
          <label className="text-sm font-semibold text-slate-800">Busca</label>
          <input
            type="text"
            placeholder="Buscar por nome ou key id..."
            value={searchTerm}
            onChange={(event) => {
              setSearchTerm(event.target.value);
              setPage(1);
            }}
            className="block w-full rounded-md border-0 py-1.5 pl-3 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-indigo-600 sm:text-sm"
          />
        </div>

        {isAdminMaster ? (
          <div className="space-y-1 lg:min-w-0">
            <label className="text-sm font-semibold text-slate-800">Empresa</label>
            <select
              value={selectedCompanyId}
              onChange={(event) => {
                const nextCompanyId = event.target.value;
                setSelectedCompanyId(nextCompanyId);
                setSelectedRestaurantId('');
                setCompanyId(nextCompanyId || null);
                setRestaurantId(null);
                setPage(1);
              }}
              className="block w-full rounded-md border-0 py-1.5 pl-3 pr-8 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-indigo-600 sm:text-sm"
            >
              <option value="">Todas as empresas</option>
              {companies.map((company) => (
                <option key={company.id} value={company.id}>
                  {company.name}
                </option>
              ))}
            </select>
          </div>
        ) : null}

        <div className="space-y-1 lg:min-w-0">
          <label className="text-sm font-semibold text-slate-800">Restaurante</label>
          <select
            value={selectedRestaurantId}
            onChange={(event) => {
              const nextRestaurantId = event.target.value;
              setSelectedRestaurantId(nextRestaurantId);
              setRestaurantId(nextRestaurantId || null);
              setPage(1);
            }}
            className="block w-full rounded-md border-0 py-1.5 pl-3 pr-8 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-indigo-600 sm:text-sm"
          >
            <option value="">Todos os restaurantes</option>
            {restaurants.map((restaurant) => (
              <option key={restaurant.id} value={restaurant.id}>
                {restaurant.name}
              </option>
            ))}
          </select>
        </div>

        <div className="flex h-[38px] items-center gap-3 whitespace-nowrap">
          <Switch
            checked={!includeInactive}
            onChange={(checked) => {
              setIncludeInactive(!checked);
              setPage(1);
            }}
            className={`${!includeInactive ? 'bg-indigo-600' : 'bg-gray-200'} relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-0`}
          >
            <span className="sr-only">Apenas ativas</span>
            <span
              aria-hidden="true"
              className={`${!includeInactive ? 'translate-x-5' : 'translate-x-0'} pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out`}
            />
          </Switch>
          <span className="text-sm text-gray-600">Apenas ativas</span>
        </div>
      </div>

      <div className="space-y-3 sm:hidden">
        {loading ? (
          <div className="rounded-lg border border-gray-200 bg-white p-4 text-sm text-gray-600">Carregando credenciais...</div>
        ) : credentials.items.length === 0 ? (
          <div className="rounded-lg border border-dashed border-gray-300 bg-gray-50 p-4 text-sm text-gray-600">Nenhuma credencial encontrada.</div>
        ) : (
          credentials.items.map((item) => (
            <article key={item.id} className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
              <div className="flex items-start justify-between gap-2">
                <div>
                  <h3 className="text-sm font-semibold text-gray-900">{item.name}</h3>
                  <p className="text-xs text-gray-500">{item.companyName || 'Empresa não identificada'}</p>
                  <p className="text-xs text-gray-500 break-all">{item.keyId}</p>
                </div>
                <StatusPill label={item.isActive ? 'Ativa' : 'Revogada'} variant={item.isActive ? 'success' : 'muted'} />
              </div>
              <p className="mt-2 text-xs text-gray-600">Restaurantes: {item.restaurants?.length ? item.restaurants.map((restaurant) => restaurant.name).join(', ') : 'Todos da empresa'}</p>
              <div className="mt-3 flex flex-wrap gap-2">
                <button type="button" onClick={() => openView(item)} className="rounded-md border border-gray-300 px-3 py-1.5 text-xs font-semibold text-gray-700 hover:bg-gray-50">Ver</button>
                <button type="button" onClick={() => openEdit(item)} className="rounded-md border border-gray-300 px-3 py-1.5 text-xs font-semibold text-gray-700 hover:bg-gray-50">Editar</button>
                <button type="button" onClick={() => rotateCredential(item)} className="rounded-md border border-indigo-200 px-3 py-1.5 text-xs font-semibold text-indigo-700 hover:bg-indigo-50">Rotacionar</button>
                <button type="button" onClick={() => deleteCredential(item)} className="rounded-md border border-red-200 px-3 py-1.5 text-xs font-semibold text-red-700 hover:bg-red-50">Excluir</button>
                {item.isActive ? (
                  <button type="button" onClick={() => revokeCredential(item)} className="rounded-md border border-rose-200 px-3 py-1.5 text-xs font-semibold text-rose-700 hover:bg-rose-50">Revogar</button>
                ) : (
                  <button type="button" onClick={() => activateCredential(item)} className="rounded-md border border-emerald-200 px-3 py-1.5 text-xs font-semibold text-emerald-700 hover:bg-emerald-50">Reativar</button>
                )}
              </div>
            </article>
          ))
        )}
      </div>

      <div className="hidden overflow-x-auto rounded-lg bg-white shadow ring-1 ring-black ring-opacity-5 sm:block">
        {loading ? (
          <div className="p-4 text-sm text-gray-600">Carregando credenciais...</div>
        ) : (
          <>
            <table className="min-w-full table-fixed divide-y divide-gray-300">
              <thead className="bg-gray-50">
                <tr>
                  <SortableTh className="w-56 py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6" sortKey="name" currentSort={sortConfig} onSort={handleSort}>Nome</SortableTh>
                  {isAdminMaster ? <th className="w-36 px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Empresa</th> : null}
                  <th className="w-48 px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Key ID</th>
                  <th className="w-72 px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Restaurantes</th>
                  <SortableTh className="w-32 px-3 py-3.5 text-left text-sm font-semibold text-gray-900" sortKey="lastUsedAt" currentSort={sortConfig} onSort={handleSort}>Último uso</SortableTh>
                  <SortableTh className="w-32 px-3 py-3.5 text-left text-sm font-semibold text-gray-900" sortKey="expiresAt" currentSort={sortConfig} onSort={handleSort}>Expira em</SortableTh>
                  <SortableTh className="w-24 px-3 py-3.5 text-left text-sm font-semibold text-gray-900" sortKey="isActive" currentSort={sortConfig} onSort={handleSort}>Status</SortableTh>
                  <th className="relative w-48 py-3.5 pl-3 pr-4 sm:pr-6">
                    <span className="sr-only">Ações</span>
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 bg-white">
                {credentials.items.length === 0 ? (
                  <tr>
                    <td colSpan={isAdminMaster ? 8 : 7} className="py-8 text-center text-sm text-gray-500">Nenhuma credencial encontrada com os filtros atuais.</td>
                  </tr>
                ) : (
                  credentials.items.map((item) => (
                    <tr key={item.id}>
                      <td className="py-4 pl-4 pr-3 text-sm font-medium text-gray-900 sm:pl-6">
                        <div className="flex items-center gap-2">
                          <KeyIcon className="h-4 w-4 text-gray-400" />
                          <span className="block truncate" title={item.name}>{item.name}</span>
                        </div>
                      </td>
                      {isAdminMaster ? <td className="px-3 py-4 text-sm text-gray-500"><span className="block truncate" title={item.companyName || '-'}>{item.companyName || '-'}</span></td> : null}
                      <td className="px-3 py-4 text-sm text-gray-500">
                        <span className="block truncate font-mono text-xs" title={item.keyId}>{item.keyId}</span>
                      </td>
                      <td className="px-3 py-4 text-sm text-gray-500">
                        <span className="block truncate" title={item.restaurants?.length ? item.restaurants.map((restaurant) => restaurant.name || restaurantNameById.get(restaurant.id) || 'Removido').join(', ') : 'Todos da empresa'}>
                          {item.restaurants?.length ? item.restaurants.map((restaurant) => restaurant.name || restaurantNameById.get(restaurant.id) || 'Removido').join(', ') : 'Todos da empresa'}
                        </span>
                      </td>
                      <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">{formatDateTime(item.lastUsedAt)}</td>
                      <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">{formatDateTime(item.expiresAt)}</td>
                      <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                        <StatusPill label={item.isActive ? 'Ativa' : 'Revogada'} variant={item.isActive ? 'success' : 'muted'} />
                      </td>
                      <td className="whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm font-medium sm:pr-6">
                        <button
                          type="button"
                          onClick={() => openView(item)}
                          className="mr-3 text-gray-500 hover:text-gray-700"
                          title="Visualizar"
                        >
                          <EyeIcon className="h-5 w-5" />
                        </button>

                        <button
                          type="button"
                          onClick={() => openEdit(item)}
                          className="mr-3 text-slate-600 hover:text-slate-900"
                          title="Editar"
                        >
                          <PencilIcon className="h-5 w-5" />
                        </button>

                        <button
                          type="button"
                          onClick={() => rotateCredential(item)}
                          className="mr-3 text-indigo-600 hover:text-indigo-900"
                          title="Rotacionar"
                        >
                          <ArrowPathIcon className="h-5 w-5" />
                        </button>

                        <button
                          type="button"
                          onClick={() => deleteCredential(item)}
                          className="mr-3 text-red-600 hover:text-red-800"
                          title="Excluir"
                        >
                          <TrashIcon className="h-5 w-5" />
                          <span className="sr-only">Excluir</span>
                        </button>

                        {item.isActive ? (
                          <button
                            type="button"
                            onClick={() => revokeCredential(item)}
                            className="text-rose-600 hover:text-rose-800"
                            title="Revogar"
                          >
                            <XCircleIcon className="h-5 w-5" />
                          </button>
                        ) : (
                          <button
                            type="button"
                            onClick={() => activateCredential(item)}
                            className="text-emerald-600 hover:text-emerald-800"
                            title="Reativar"
                          >
                            <CheckCircleIcon className="h-5 w-5" />
                          </button>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>

            {credentials.totalItems > 0 ? (
              <Pagination
                currentPage={page}
                totalPages={credentials.totalPages || 1}
                onPageChange={setPage}
                totalItems={credentials.totalItems}
                pageSize={pageSize}
              />
            ) : null}
          </>
        )}
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <div className="min-w-0 space-y-3 rounded-xl border border-slate-200 bg-white p-3 shadow-sm sm:p-4">
          <h3 className="text-lg font-semibold text-slate-900">Estrutura da assinatura</h3>
          <p className="text-sm text-slate-600">Monte a mensagem com método, caminho, timestamp e corpo, depois gere o HMAC.</p>
          <pre className="max-w-full overflow-x-auto rounded-lg bg-slate-900 p-3 text-xs leading-5 text-slate-100 sm:p-4 sm:text-sm">{messageTemplate}</pre>
          <CopyButton text={messageTemplate} onCopy={notify} />
        </div>

        <div className="min-w-0 space-y-3 rounded-xl border border-slate-200 bg-white p-3 shadow-sm sm:p-4">
          <h3 className="text-lg font-semibold text-slate-900">Exemplo de requisição</h3>
          <p className="text-sm text-slate-600">Para GET, use corpo vazio na assinatura e mantenha o mesmo PATH+QUERY usado na URL.</p>
          <pre className="max-w-full overflow-x-auto rounded-lg bg-slate-900 p-3 text-xs leading-5 text-slate-100 sm:p-4 sm:text-sm">{curlExample}</pre>
          <CopyButton text={curlExample} onCopy={notify} />
        </div>
      </div>

      <Dialog open={modalOpen} onClose={closeModal} className="relative z-50">
        <div className="fixed inset-0 bg-gray-500/60" aria-hidden="true" />
        <div className="fixed inset-0 overflow-y-auto p-4">
          <Dialog.Panel className="mx-auto w-full max-w-2xl rounded-2xl bg-white p-6 shadow-xl">
            <Dialog.Title className="text-lg font-semibold text-gray-900">
              {modalMode === 'create' ? 'Nova credencial de integração' : modalMode === 'edit' ? 'Editar credencial' : 'Detalhes da credencial'}
            </Dialog.Title>

            {modalMode === 'view' ? (
              <div className="mt-4 space-y-3 rounded-lg border border-slate-200 bg-slate-50 p-4 text-sm text-slate-700">
                <p><span className="font-semibold">Nome:</span> {activeCredential?.name}</p>
                <p><span className="font-semibold">Empresa:</span> {activeCredential?.companyName || '-'}</p>
                <p><span className="font-semibold">Key ID:</span> <span className="font-mono text-xs">{activeCredential?.keyId}</span></p>
                <p><span className="font-semibold">Restaurantes:</span> {activeCredential?.restaurants?.length ? activeCredential.restaurants.map((restaurant) => restaurant.name).join(', ') : 'Todos da empresa'}</p>
                <div>
                  <p><span className="font-semibold">Métodos permitidos:</span></p>
                  <MethodPills scopes={activeCredential?.scopes} />
                </div>
                <p><span className="font-semibold">Status:</span> {activeCredential?.isActive ? 'Ativa' : 'Revogada'}</p>
                <p><span className="font-semibold">Último uso:</span> {formatDateTime(activeCredential?.lastUsedAt)}</p>
                <p><span className="font-semibold">Expira em:</span> {formatDateTime(activeCredential?.expiresAt)}</p>
              </div>
            ) : (
              <form className="mt-4 space-y-4" onSubmit={submitCredential}>
                {isAdminMaster ? (
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Empresa <span className="text-red-500">*</span></label>
                    <select
                      value={formData.companyId}
                      onChange={(event) => {
                        const nextCompanyId = event.target.value;
                        setFormData((current) => ({ ...current, companyId: nextCompanyId, restaurantIds: [] }));
                        setFormErrors((current) => ({ ...current, companyId: '' }));
                        loadRestaurants(nextCompanyId);
                      }}
                      className={`mt-1 block w-full rounded-md border-0 py-1.5 px-3 text-gray-900 shadow-sm ring-1 ring-inset ${formErrors.companyId ? 'ring-red-300 focus:ring-red-500' : 'ring-gray-300 focus:ring-indigo-600'} focus:ring-2 sm:text-sm`}
                      required
                    >
                      <option value="">Selecione</option>
                      {companies.map((company) => (
                        <option key={company.id} value={company.id}>{company.name}</option>
                      ))}
                    </select>
                    {formErrors.companyId ? <p className="mt-1 text-xs text-red-600">{formErrors.companyId}</p> : null}
                  </div>
                ) : null}

              <div>
                <label className="block text-sm font-medium text-gray-700">Nome <span className="text-red-500">*</span></label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(event) => {
                    setFormData((current) => ({ ...current, name: event.target.value }));
                    if (formErrors.name) {
                      setFormErrors((current) => ({ ...current, name: '' }));
                    }
                  }}
                  className={`mt-1 block w-full rounded-md border-0 py-1.5 px-3 text-gray-900 shadow-sm ring-1 ring-inset ${formErrors.name ? 'ring-red-300 focus:ring-red-500' : 'ring-gray-300 focus:ring-indigo-600'} focus:ring-2 sm:text-sm`}
                  placeholder="Ex.: PDV Parceiro A"
                  required
                />
                {formErrors.name ? <p className="mt-1 text-xs text-red-600">{formErrors.name}</p> : null}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Restaurantes com acesso</label>
                <div className="mt-1 max-h-40 space-y-2 overflow-y-auto rounded-md border border-gray-300 px-3 py-2">
                  {restaurants.length === 0 ? (
                    <p className="text-sm text-gray-500">Nenhum restaurante ativo para a empresa selecionada.</p>
                  ) : (
                    restaurants.map((restaurant) => (
                      <label key={restaurant.id} className="flex items-center gap-2 text-sm text-gray-700">
                        <input
                          type="checkbox"
                          checked={formData.restaurantIds.includes(restaurant.id)}
                          onChange={() => toggleRestaurant(restaurant.id)}
                          className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-600"
                        />
                        {restaurant.name}
                      </label>
                    ))
                  )}
                </div>
                <p className="mt-1 text-xs text-gray-500">Sem seleção: acesso a todos os restaurantes da empresa.</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Expiração (opcional)</label>
                <input
                  type="datetime-local"
                  value={formData.expiresAt}
                  onChange={(event) => setFormData((current) => ({ ...current, expiresAt: event.target.value }))}
                  className="mt-1 block w-full rounded-md border-0 py-1.5 px-3 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-indigo-600 sm:text-sm"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Métodos permitidos <span className="text-red-500">*</span></label>
                <div className="mt-1 grid gap-2 rounded-md border border-gray-300 px-3 py-2 sm:grid-cols-2">
                  {METHOD_OPTIONS.map((option) => (
                    <label key={option.value} className="flex items-center gap-2 text-sm text-gray-700">
                      <input
                        type="checkbox"
                        checked={formData.scopes.includes(option.value)}
                        onChange={() => {
                          toggleScope(option.value);
                          if (formErrors.scopes) {
                            setFormErrors((current) => ({ ...current, scopes: '' }));
                          }
                        }}
                        className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-600"
                      />
                      {option.label}
                    </label>
                  ))}
                </div>
                {formErrors.scopes ? <p className="mt-1 text-xs text-red-600">{formErrors.scopes}</p> : null}
              </div>

              <div className="flex items-center justify-between rounded-md border border-slate-200 px-3 py-2">
                <span className="text-sm text-gray-600">Credencial ativa</span>
                <Switch
                  checked={formData.isActive}
                  onChange={(checked) => setFormData((current) => ({ ...current, isActive: checked }))}
                  className={`${formData.isActive ? 'bg-indigo-600' : 'bg-gray-200'} relative inline-flex h-6 w-11 items-center rounded-full`}
                >
                  <span
                    className={`${formData.isActive ? 'translate-x-6' : 'translate-x-1'} inline-block h-4 w-4 transform rounded-full bg-white transition`}
                  />
                </Switch>
              </div>

              <div className="flex justify-end gap-3 pt-2">
                <button
                  type="button"
                  onClick={closeModal}
                  className="rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 ring-1 ring-gray-300 hover:bg-gray-50"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 disabled:opacity-60"
                >
                  {saving ? 'Salvando...' : modalMode === 'edit' ? 'Salvar alterações' : 'Gerar credencial'}
                </button>
              </div>
            </form>
            )}

            {modalMode === 'view' ? (
              <div className="mt-5 flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => {
                    if (!activeCredential) return;
                    deleteCredential(activeCredential);
                    closeModal();
                  }}
                  className="rounded-md border border-red-200 bg-white px-3 py-2 text-sm font-semibold text-red-700 hover:bg-red-50"
                >
                  Excluir credencial
                </button>
                <button
                  type="button"
                  onClick={closeModal}
                  className="rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white hover:bg-indigo-500"
                >
                  Fechar
                </button>
              </div>
            ) : null}
          </Dialog.Panel>
        </div>
      </Dialog>

      <Dialog open={Boolean(issuedSecret)} onClose={() => setIssuedSecret(null)} className="relative z-50">
        <div className="fixed inset-0 bg-gray-500/60" aria-hidden="true" />
        <div className="fixed inset-0 flex items-center justify-center p-4">
          <Dialog.Panel className="w-full max-w-2xl rounded-2xl bg-white p-6 shadow-xl">
            <Dialog.Title className="text-lg font-semibold text-gray-900">Segredo gerado</Dialog.Title>
            <p className="mt-2 text-sm text-gray-600">Guarde este segredo agora. Ele não poderá ser consultado novamente.</p>
            {issuedSecret ? (
              <div className="mt-4 space-y-3">
                <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm">
                  <p><span className="font-semibold">Nome:</span> {issuedSecret.name}</p>
                  <p><span className="font-semibold">API Key:</span> <span className="font-mono">{issuedSecret.keyId}</span></p>
                  <div className="mt-3">
                    <CopyButton text={issuedSecret.keyId} onCopy={notify}>Copiar API Key</CopyButton>
                  </div>
                </div>
                <div>
                  <p className="mb-2 text-sm font-semibold text-gray-700">API Secret:</p>
                  <pre className="overflow-x-auto rounded-lg bg-slate-900 p-4 text-xs text-slate-100">{issuedSecret.secret}</pre>
                </div>
                <CopyButton text={issuedSecret.secret} onCopy={notify}>Copiar API Secret</CopyButton>
              </div>
            ) : null}

            <div className="mt-5 flex justify-end">
              <button
                type="button"
                onClick={() => setIssuedSecret(null)}
                className="rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white hover:bg-indigo-500"
              >
                Entendi
              </button>
            </div>
          </Dialog.Panel>
        </div>
      </Dialog>

      {snackbar ? <div className="fixed right-6 bottom-6 rounded-lg bg-slate-900 px-4 py-3 text-sm font-semibold text-white shadow-lg">{snackbar}</div> : null}

      <ContextHelpDrawer
        open={isHelpOpen}
        onClose={() => setIsHelpOpen(false)}
        title="Integrações"
        description="Revise segurança de credenciais, escopos e assinatura HMAC antes de liberar acesso externo."
        sections={INTEGRATIONS_HELP_SECTIONS}
        activeSectionKey={helpSectionKey}
        onSectionChange={setHelpSectionKey}
      />
    </section>
  );
};

export default IntegrationsPage;
